import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { connectSocket } from '../../../utils/socket';
import axios from 'axios';
import { baseAPI } from '../../../config';
import useManagementStore from '../../../store/managementStore';
import userStore from '../../../store/userStore';
import { expedientExistsInList } from '../utils/assignmentUtils';
import {
  buildFilterQueryParams,
  assignmentMatchesActiveFilter,
} from '../utils/buildFilterQueryParams';

const LIMIT = 15;

/**
 * Hook to manage socket connection and handle real-time updates
 */
export const useSocketManagement = (currentPage, totalPages, setSelectedExpedient) => {
  const { addInteraction, updateInteractionStatus, upsertExpedient } = useManagementStore();
  const socketRef = useRef(null);
  const [searchParams] = useSearchParams();
  const currentUserId = userStore(state => state.user?.id);

  // Snapshot the latest values so the socket effect can run once and still
  // see fresh state without re-subscribing on every render.
  const searchParamsRef = useRef(searchParams);
  const currentPageRef = useRef(currentPage);
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    const newSocket = connectSocket();
    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      newSocket.emit('join_management');
    });

    newSocket.on('ack_update', response => {
      const { data } = response;
      updateInteractionStatus(data.interaction.expedient_id, data.msgId, data.ackType);
    });

    // New interaction: only update the affected expedient if it is already
    // in the visible list. Whether the expedient should appear in the list
    // depends on whether a matching Assignment was created — that is handled
    // by the new_assignment event below.
    newSocket.on('new_interaction', response => {
      const expedientId = response?.expedientId;
      const interaction = response?.data;
      if (!expedientId || !interaction) return;

      const { expedientsList } = useManagementStore.getState();
      if (expedientExistsInList(expedientsList, expedientId)) {
        addInteraction(expedientId, interaction);
      }
    });

    // New assignment: if it does not match the active filter, ignore. If it
    // matches and the expedient is already on the page, ignore (counts socket
    // takes care of the chip). Otherwise fetch only that expedient with the
    // active filter applied and merge it into the list.
    newSocket.on('new_assignment', async response => {
      const expedientId = response?.expedientId;
      const assignment = response?.data;
      if (!expedientId || !assignment) return;

      const params = searchParamsRef.current;
      const me = currentUserIdRef.current;
      if (!assignmentMatchesActiveFilter(params, assignment, { currentUserId: me })) return;

      const { expedientsList } = useManagementStore.getState();
      if (expedientExistsInList(expedientsList, expedientId)) return;

      try {
        const queryString = buildFilterQueryParams(params, {
          page: currentPageRef.current,
          limit: LIMIT,
          expedientId,
          currentUserId: me,
        });
        const { data } = await axios.get(`${baseAPI}/assignments/expedients?${queryString}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const fetched = Array.isArray(data?.expedients) ? data.expedients[0] : null;
        if (fetched) upsertExpedient(fetched);
      } catch (err) {
        console.error('Error fetching expedient for new assignment:', err);
      }
    });

    return () => {
      newSocket.emit('leave_management');
      newSocket.close();
    };
    // setSelectedExpedient is kept in deps for backwards compatibility with the
    // page wiring, but the handlers read state via refs so they always see
    // fresh filters/page without reconnecting.
  }, [addInteraction, updateInteractionStatus, upsertExpedient, setSelectedExpedient]);

  return socketRef.current;
};
